/**
 * THE LIVE SESSION, SEEN FROM THE CLIENT (sweep 2026-10-05). The server re-checks a claimed account's session epoch on
 * every reducer that moves value (`requireLiveSession`, 33 of them) and refuses a stale one with a message that starts
 * `auth:session`. Each call site handles its own refusal (THE REJECTION LAW), so a toast said "auth:session — …" and the
 * player kept playing on a connection that could no longer trade. This wraps every reducer once, at connect, and reports
 * a session refusal — the caller still receives its rejection unchanged.
 */

/** True for the server's live-session refusal, whatever wrapper the SDK puts round the message. */
export function isSessionRefusal(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  return msg.includes('auth:session');
}

/**
 * Wrap each reducer function on `reducers` (in place). `onEnded` is called at most once per `quietMs` however many calls
 * are refused together. Returns how many functions were wrapped.
 */
export function watchReducers(reducers: object, onEnded: () => void, quietMs = 10_000, now: () => number = () => Date.now()): number {
  const r = reducers as Record<string, unknown>;
  let lastAt = -Infinity;
  let n = 0;
  for (const key of Object.keys(r)) {
    const fn = r[key];
    if (typeof fn !== 'function') continue;
    r[key] = (...args: unknown[]): unknown => {
      const out = (fn as (...a: unknown[]) => unknown)(...args);
      if (!out || typeof (out as Promise<unknown>).then !== 'function') return out;
      // The CHAINED promise goes back, rethrowing: the caller still gets exactly one rejection, and one nobody catches
      // still surfaces as an unhandled rejection (a side branch would have handled every reducer's error for good).
      return (out as Promise<unknown>).then(undefined, (e: unknown) => {
        if (isSessionRefusal(e)) {
          const t = now();
          if (t - lastAt >= quietMs) { lastAt = t; onEnded(); }
        }
        throw e;
      });
    };
    n++;
  }
  return n;
}
